import type { DocumentsParams, DocumentsQueryInput } from '../-types';

/**
 * The columns the admin lists can order by —
 * `ADMIN_COMPANY_DOCUMENT_LIST_SORT_COLUMNS` in `modules/company-documents`.
 * Any other column is a 400, so dropping it here keeps a header click from
 * failing the list.
 */
const SORTABLE_FIELDS = new Set([
  'id',
  'name',
  'companyName',
  'createdBy',
  'type',
  'updatedAt',
]);

/**
 * The columns the admin lists filter by that this table offers. The routes
 * also match `companyId`; any other key is ignored. The old dashboard offered a
 * Created By filter box that never narrowed anything; leaving the field out is
 * the honest version of that.
 */
const FILTERABLE_FIELDS = new Set(['id', 'name', 'companyName', 'type']);

/**
 * Turns table state into the documents endpoints' parameters.
 *
 * `page` converts from the UI's 1-based count to the endpoint's 0-based one.
 * `filter` is a JSON object of `column: value`, capped at 512 characters by
 * the route.
 *
 * Note `type` filters on `document_type_id`, not the displayed title. The
 * routes take comma-separated ids, but the filter is single-select here.
 */
export function toDocumentsParams(input: DocumentsQueryInput): DocumentsParams {
  const filter: Record<string, string> = {};
  for (const [key, values] of Object.entries(input.filters ?? {})) {
    const value = values?.[0];
    if (FILTERABLE_FIELDS.has(key) && value) filter[key] = value;
  }

  // Dropped rather than forwarded: an unsupported field would error the list.
  const [field] = (input.sort ?? '').split(':');
  const sort = field && SORTABLE_FIELDS.has(field) ? input.sort : undefined;

  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    ...(sort ? { sort } : {}),
    ...(Object.keys(filter).length > 0
      ? { filter: JSON.stringify(filter) }
      : {}),
    isArchived: input.isArchived,
  };
}

export function buildDocumentsQs(params: DocumentsParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}
